import type { OrderAction, OrderStatus } from '@telestore/contracts';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { orderStatusLabel } from '@telestore/i18n';
import { allowedTransitions, statusTimeline } from '@telestore/order-core';
import { formatPhnomPenh, provinceName } from '@telestore/shared';
import { BanknoteArrowUp, CheckCircle2, Flag, HandCoins, Landmark, PackageCheck, Truck } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  Empty,
  ErrorBox,
  Input,
  Money,
  ORDER_STATUS_TONE,
  Panel,
  Pill,
  Row,
  Rows,
  Spinner,
} from '../components/ui.js';

const FILTERS: Array<{ value: OrderStatus | 'all'; labelKey: string }> = [
  { value: 'all', labelKey: 'common.all' },
  { value: 'pending_confirm', labelKey: 'admin.pendingConfirm' },
  { value: 'awaiting_payment', labelKey: 'admin.awaitingPayment' },
  { value: 'paid', labelKey: 'order.status.paid' },
  { value: 'shipped', labelKey: 'order.status.shipped' },
];

export function OrdersPage() {
  const { shopId, locale, t } = useSession();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<OrderStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  const orders = useInfiniteQuery({
    queryKey: ['orders', shopId, filter, search],
    enabled: Boolean(shopId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.orders(shopId!, {
        ...(filter !== 'all' ? { status: filter } : {}),
        ...(search ? { q: search } : {}),
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = orders.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        {FILTERS.map((entry) => (
          <Button
            key={entry.value}
            variant="ghost"
            size="sm"
            className={
              filter === entry.value
                ? 'min-h-8 rounded-full bg-primary px-3 text-xs text-primary-foreground'
                : 'min-h-8 rounded-full border border-border bg-card px-3 text-xs text-muted-foreground'
            }
            onClick={() => setFilter(entry.value)}
          >
            {t(entry.labelKey as never)}
          </Button>
        ))}
        <Input
          className="ml-auto max-w-48"
          placeholder={t('common.search')}
          value={search}
          onChange={(event) => setSearch(event.target.value.toUpperCase())}
          aria-label={t('common.search')}
        />
      </div>

      <Panel padded={false}>
        {orders.isPending ? (
          <Spinner label={t('common.loading')} />
        ) : orders.isError ? (
          <ErrorBox message={(orders.error as Error).message} onRetry={() => void orders.refetch()} />
        ) : items.length === 0 ? (
          <Empty title={t('common.empty')} />
        ) : (
          <Rows>
            {items.map((order) => (
              <Row
                key={order.id}
                onClick={() => void navigate({ to: '/orders/$orderId', params: { orderId: order.id } })}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-semibold">{order.code}</span>
                    <Pill tone={ORDER_STATUS_TONE[order.status]}>
                      {orderStatusLabel(locale, order.status)}
                    </Pill>
                    {order.paymentType === 'cod' ? <Pill tone="neutral">COD</Pill> : null}
                  </div>
                  <p className="truncate text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {order.customerName ?? '—'} · {order.itemCount} ·{' '}
                    {order.placedAt ? formatPhnomPenh(new Date(order.placedAt), locale) : ''}
                  </p>
                </div>
                <Money amount={order.total.amount} currency={order.currency} className="font-semibold tabular-nums" />
              </Row>
            ))}
          </Rows>
        )}
      </Panel>

      {orders.hasNextPage ? (
        <Button variant="outline" className="self-center" disabled={orders.isFetchingNextPage} onClick={() => void orders.fetchNextPage()} >
          {orders.isFetchingNextPage ? t('common.loading') : t('common.next')}
        </Button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Detail                                                              */
/* ------------------------------------------------------------------ */

export function OrderDetailPage() {
  const { orderId } = useParams({ from: '/orders/$orderId' });
  const { shopId, locale, t } = useSession();
  const queryClient = useQueryClient();
  const [actionError, setActionError] = useState<string | null>(null);
  const [trackingCode, setTrackingCode] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const query = useQuery({
    queryKey: ['order', shopId, orderId],
    queryFn: () => api.order(shopId!, orderId),
    enabled: Boolean(shopId),
  });

  const act = useMutation({
    mutationFn: (action: OrderAction) => api.orderAction(shopId!, orderId, action),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({ queryKey: ['order', shopId, orderId] });
      void queryClient.invalidateQueries({ queryKey: ['orders', shopId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard', shopId] });
    },
    onError: (error) => {
      setActionError(error instanceof ApiError ? error.message : t('errors.internal'));
    },
  });

  if (query.isPending) return <Spinner label={t('common.loading')} />;
  if (query.isError || !query.data) {
    return <ErrorBox message={(query.error as Error | undefined)?.message ?? t('errors.not_found')} />;
  }

  const { order } = query.data;
  /**
   * Buttons are derived from the state machine itself, so a merchant is never
   * offered an action the server would reject.
   */
  const next = allowedTransitions(order.status, order.paymentType);
  const timeline = statusTimeline(order.paymentType);
  const currentIndex = timeline.indexOf(order.status);

  return (
    <div className="flex flex-col gap-4">
      <Panel>
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-lg font-bold">{order.code}</span>
          <Pill tone={ORDER_STATUS_TONE[order.status]}>{orderStatusLabel(locale, order.status)}</Pill>
          <Pill tone="neutral">{order.paymentType === 'cod' ? 'COD' : 'KHQR'}</Pill>
          <span className="ml-auto text-xs" style={{ color: 'var(--color-ink-soft)' }}>
            {order.placedAt ? formatPhnomPenh(new Date(order.placedAt), locale) : ''}
          </span>
        </div>

        <ol className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs">
          {timeline.map((status, index) => (
            <li
              key={status}
              style={{
                color: currentIndex >= index ? 'var(--color-ink)' : 'var(--color-ink-faint)',
                fontWeight: currentIndex === index ? 700 : 400,
              }}
            >
              {currentIndex > index ? '✓ ' : ''}
              {orderStatusLabel(locale, status)}
            </li>
          ))}
        </ol>
      </Panel>

      {actionError ? <ErrorBox message={actionError} /> : null}

      {next.length > 0 ? (
        <Panel title={t('merchant.viewOrder')}>
          <div className="flex flex-wrap gap-2">
            {next.includes('awaiting_payment') || next.includes('confirmed') ? (
              <Button variant="positive" disabled={act.isPending} onClick={() => act.mutate({ action: 'confirm' })} >
                <CheckCircle2 />
                {t('merchant.confirmOrder')}
              </Button>
            ) : null}

            {next.includes('paid') ? (
              <Button variant="positive" disabled={act.isPending} onClick={() => act.mutate({ action: 'mark_paid' })} >
                <BanknoteArrowUp />
                {t('merchant.markPaid')}
              </Button>
            ) : null}

            {next.includes('fulfilling') ? (
              <Button disabled={act.isPending} onClick={() => act.mutate({ action: 'start_fulfilling' })} >
                <PackageCheck />
                {t('admin.readyToShip')}
              </Button>
            ) : null}

            {next.includes('shipped') ? (
              <div className="flex items-center gap-2">
                <Input
                  className="max-w-44"
                  placeholder="Tracking code"
                  value={trackingCode}
                  onChange={(event) => setTrackingCode(event.target.value)}
                />
                <Button disabled={act.isPending} onClick={() => act.mutate({ action: 'ship', ...(trackingCode.trim() ? { trackingCode: trackingCode.trim() } : {}), }) } >
                  <Truck />
                  {t('merchant.markShipped')}
                </Button>
              </div>
            ) : null}

            {next.includes('collected') ? (
              <Button variant="positive" disabled={act.isPending} onClick={() => act.mutate({ action: 'mark_collected' })} >
                <HandCoins />
                {t('merchant.markCollected')}
              </Button>
            ) : null}

            {next.includes('remitted') ? (
              <Button variant="positive" disabled={act.isPending} onClick={() => act.mutate({ action: 'mark_remitted' })} >
                <Landmark />
                {t('merchant.markRemitted')}
              </Button>
            ) : null}

            {next.includes('completed') ? (
              <Button disabled={act.isPending} onClick={() => act.mutate({ action: 'complete' })} >
                <Flag />
                {t('merchant.markCompleted')}
              </Button>
            ) : null}
          </div>

          {next.includes('cancelled') ? (
            <div className="mt-4 flex items-center gap-2">
              <Input
                className="max-w-64"
                placeholder={t('order.cancelOrder')}
                value={cancelReason}
                onChange={(event) => setCancelReason(event.target.value)}
              />
              <Button variant="outline" className="text-destructive" disabled={act.isPending || cancelReason.trim().length < 2} onClick={() => act.mutate({ action: 'cancel', reason: cancelReason.trim() })} >
                ✖️ {t('order.cancelOrder')}
              </Button>
            </div>
          ) : null}
        </Panel>
      ) : null}

      <Panel title={t('order.items')} padded={false}>
        <Rows>
          {order.items.map((item) => (
            <Row key={item.id}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm">
                  {item.snapshot.productName[locale] || item.snapshot.productName.en}
                </p>
                <p className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                  {[
                    Object.values(item.snapshot.options).join(' / '),
                    item.snapshot.sku,
                    `× ${item.quantity}`,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <Money amount={item.lineTotal.amount} currency={order.currency} className="tabular-nums" />
            </Row>
          ))}
        </Rows>
        <div className="flex flex-col gap-1 p-4 text-sm">
          <div className="flex justify-between" style={{ color: 'var(--color-ink-soft)' }}>
            <span>{t('common.subtotal')}</span>
            <Money amount={order.subtotal.amount} currency={order.currency} />
          </div>
          {order.shipping.amount > 0 ? (
            <div className="flex justify-between" style={{ color: 'var(--color-ink-soft)' }}>
              <span>{t('common.shipping')}</span>
              <Money amount={order.shipping.amount} currency={order.currency} />
            </div>
          ) : null}
          <div className="flex justify-between text-base font-bold">
            <span>{t('common.total')}</span>
            <Money amount={order.total.amount} currency={order.currency} />
          </div>
          {/*
            The charge amount differs from the total by the unique offset. The
            merchant must see it, because it is what will appear on their bank
            statement.
          */}
          {order.chargeAmount.amount !== order.total.amount ? (
            <div className="flex justify-between text-sm" style={{ color: 'var(--color-warn)' }}>
              <span>{t('payment.transferExactly', { amount: '' })}</span>
              <Money amount={order.chargeAmount.amount} currency={order.currency} />
            </div>
          ) : null}
        </div>
      </Panel>

      {order.delivery ? (
        <Panel title={t('checkout.deliveryAddress')}>
          <p className="text-sm font-medium">{order.delivery.receiverName}</p>
          <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
            {order.delivery.phone}
          </p>
          <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
            {provinceName(order.delivery.provinceCode, locale)}
            {order.delivery.district ? `, ${order.delivery.district}` : ''}
          </p>
          <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
            {order.delivery.addressLine}
          </p>
          {order.delivery.note ? <p className="mt-2 text-sm">📝 {order.delivery.note}</p> : null}
        </Panel>
      ) : null}

      {order.attribution?.source ? (
        <Panel title="Attribution">
          <p className="text-sm" style={{ color: 'var(--color-ink-soft)' }}>
            {order.attribution.source}
            {order.attribution.ref_code ? ` · ${order.attribution.ref_code}` : ''}
          </p>
        </Panel>
      ) : null}
    </div>
  );
}
